import React, { memo, lazy, Suspense } from 'react';
import { isEmpty } from 'lodash';
import { Col, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import CardSkeleton from 'components/common/skeleton/CardSkeleton';

// Dynamically import chart components to avoid circular dependencies
const PieChartComponent = lazy(() => import('./charts/multiDataPieChart'));
const TreeChartComponent = lazy(() => import('./charts/treeChartComponent'));
const BarChartComponent = lazy(() => import('./charts/barChart'));
const BarChartWithRangeSelection = lazy(() =>
  import('./charts/zoomONValueChart')
);

const ChartComponent = memo(({ type, data }) => {
  const loadingFallback = (
    <div className="text-center py-3">
      <Spinner animation="border" size="sm" />
    </div>
  );

  switch (type) {
    case 'pie':
      return (
        <Suspense fallback={loadingFallback}>
          <PieChartComponent chartData={data} />
        </Suspense>
      );
    case 'treemap':
      return (
        <Suspense fallback={loadingFallback}>
          <TreeChartComponent data={data} />
        </Suspense>
      );
    case 'zoom_on_value':
      return (
        <Suspense fallback={loadingFallback}>
          <BarChartWithRangeSelection data={data} />
        </Suspense>
      );
    case 'bar':
    case 'seasonal_bar':
      return (
        <Suspense fallback={loadingFallback}>
          <BarChartComponent data={data} />
        </Suspense>
      );
    default:
      return null;
  }
});

ChartComponent.propTypes = {
  type: PropTypes.oneOf(['pie', 'seasonal_bar', 'bar', 'line']).isRequired,
  data: PropTypes.object.isRequired
};

const MapComponent = memo(
  ({ type, data, coordinates, legendData, keyId, info, variant, label }) => {
    switch (type) {
      case 'comparison':
        return (
          // <MonitorComparisonMap
          //   key={label}
          //   landCoverData={data}
          //   coordinates={coordinates}
          //   keyId={keyId}
          //   legendData={legendData}
          //   variant={variant}
          //   info={info}
          //   label={label}
          // />
          <></>
        );
      case 'normal':
        return (
          // <MultiYearMap
          //   key={label}
          //   data={data}
          //   coordinates={coordinates}
          //   legendData={legendData}
          //   info={info}
          //   variant={variant}
          //   label={label}
          // />
          <></>
        );
      default:
        return null;
    }
  }
);

MapComponent.propTypes = {
  type: PropTypes.oneOf(['comparison', 'normal']).isRequired,
  data: PropTypes.any.isRequired,
  coordinates: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  }), // Remove `.isRequired` if coordinates can be optional or might sometimes be undefined.
  legendData: PropTypes.any,
  keyId: PropTypes.string,
  info: PropTypes.any,
  variant: PropTypes.any,
  label: PropTypes.string
};

const ObservationRenderer = ({ dataset, loading, coordinates }) => {
  if (loading) {
    return <CardSkeleton footer={false} />;
  }

  if (isEmpty(dataset)) {
    return null;
  }

  return (
    <>
      {Object.entries(dataset).map(([key, value]) => {
        return (
          <Col className="my-2" key={key} md={value?.width || 12}>
            {value.type === 'chart' && (
              <ChartComponent type={value.type_format} data={value} />
            )}
            {value.type === 'map' && (
              <MapComponent
                type={value.type_format}
                data={value.data}
                coordinates={coordinates}
                legendData={value.legend}
                keyId={key}
                info={value.description}
                variant={value.variant}
                label={value.label}
              />
            )}
          </Col>
        );
      })}
    </>
  );
};

ObservationRenderer.propTypes = {
  dataset: PropTypes.objectOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['chart', 'map']).isRequired,
      type_format: PropTypes.string.isRequired,
      width: PropTypes.number,
      data: PropTypes.any,
      Legend: PropTypes.any
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  coordinates: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  })
};

export default ObservationRenderer;
